import { Injectable } from '@angular/core';

import { Action } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of, zip } from 'rxjs';
import { switchMap, catchError, mergeMap } from 'rxjs/operators';

import { SnackbarService } from 'app/services/snackbar.service';
import { OwnRestrictedListService } from 'modules/core/services/own-restricted-list.service';

import { BaseListReqParams } from 'modules/shared/models/base.models';
import {
  OwnRestrictedListActionTypes, GetOwnRestrictedList, GetOwnRestrictedListSuccess, GetOwnRestrictedListFailure,
  GetOwnRestrictedLists, GetOwnRestrictedListsSuccess, GetOwnRestrictedListsFailure, CreateOwnRestrictedList,
  CreateOwnRestrictedListSuccess, CreateOwnRestrictedListFailure, UpdateOwnRestrictedList, UpdateOwnRestrictedListSuccess,
  UpdateOwnRestrictedListFailure, GetRestrictedNames, GetRestrictedNamesSuccess, GetRestrictedNamesFailure,
  CreateRestrictedName, CreateRestrictedNameSuccess, CreateRestrictedNameFailure, EditRestrictedName,
  EditRestrictedNameSuccess, EditRestrictedNameFailure
} from '../actions/own-restricted-list.actions';
import { OwnRestrictedList, OwnRestrictedName } from 'modules/shared/models/own-restricted-list.models';
import {
  EditOwnRestrictedNameRequest, GetOwnRestrictedNamesRequest, EditOwnRestrictedNamePayload,
  OwnRestrictedNamesResponse, OwnRestrictedListsResponse
} from 'store/models/own-restricted-list.models';

@Injectable()
export class OwnRestrictedListEffects {

  constructor(private actions$: Actions,
              private snackBar: SnackbarService,
              private ownRestrictedListService: OwnRestrictedListService) {}

  @Effect() getOwnRestrictedList$: Observable<Action> = this.actions$.pipe(
    ofType<GetOwnRestrictedList>(OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LIST),
    mergeMap((res: GetOwnRestrictedList) => {
      return this.ownRestrictedListService.getOwnRestrictedList(res.payload);
    }),
    switchMap((res: OwnRestrictedList) => {
      return of(new GetOwnRestrictedListSuccess(res));
    }),
    catchError((error: any) => {
      return of(new GetOwnRestrictedListFailure(error.message));
    })
  );

  @Effect() getOwnRestrictedLists$: Observable<Action> = this.actions$.pipe(
    ofType<GetOwnRestrictedLists>(OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LISTS),
    mergeMap((res: GetOwnRestrictedLists) => {
      return this.ownRestrictedListService.getOwnRestrictedLists(res.payload);
    }),
    switchMap((res: OwnRestrictedListsResponse) => {
      return of(new GetOwnRestrictedListsSuccess(res));
    }),
    catchError((error: any) => {
      return of(new GetOwnRestrictedListsFailure(error.message));
    })
  );

  @Effect() createOwnRestrictedList: Observable<Action> = this.actions$.pipe(
    ofType<CreateOwnRestrictedList>(OwnRestrictedListActionTypes.CREATE_OWN_RESTRICTED_LIST),
    mergeMap((res: CreateOwnRestrictedList) => {
      return this.ownRestrictedListService.createOwnRestrictedList(res.payload);
    }),
    switchMap((res: OwnRestrictedList) => {
      return of(new CreateOwnRestrictedListSuccess(res));
    }),
    catchError((error: any) => {
      return of(new CreateOwnRestrictedListFailure(error.message));
    })
  );

  @Effect() updateOwnRestrictedList$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateOwnRestrictedList>(OwnRestrictedListActionTypes.UPDATE_OWN_RESTRICTED_LIST),
    mergeMap((res: UpdateOwnRestrictedList) => {
      return this.ownRestrictedListService.updateOwnRestrictedList(res.payload);
    }),
    switchMap((res: OwnRestrictedList) => {
      return of(new UpdateOwnRestrictedListSuccess(res));
    }),
    catchError((error: any) => {
      return of(new UpdateOwnRestrictedListFailure(error.message));
    })
  );

  @Effect() getRestrictedNames$: Observable<Action> = this.actions$.pipe(
    ofType<GetRestrictedNames>(OwnRestrictedListActionTypes.GET_RESTRICTED_NAMES),
    mergeMap((res: GetRestrictedNames) => {
      return this.ownRestrictedListService.getRestrictedNames(res.payload);
    }),
    switchMap((res: OwnRestrictedNamesResponse) => {
      return of(new GetRestrictedNamesSuccess(res));
    }),
    catchError((error: any) => {
      return of(new GetRestrictedNamesFailure(error.message));
    })
  );

  @Effect() createRestrictedName$: Observable<Action> = this.actions$.pipe(
    ofType<CreateRestrictedName>(OwnRestrictedListActionTypes.CREATE_RESTRICTED_NAME),
    mergeMap((res: CreateRestrictedName) => {
      return this.ownRestrictedListService.createRestrictedName(res.payload);
    }),
    switchMap((res: OwnRestrictedName) => {
      this.snackBar.success('Name successfully added to list');
      const getNamesRequest: GetOwnRestrictedNamesRequest = <GetOwnRestrictedNamesRequest>{
        listId: res.ownSublist,
        data: <BaseListReqParams>{
          search: '',
          sort: '-active, -updated_at',
          offset: 0,
          limit: 5,
        }
      };

      return [new CreateRestrictedNameSuccess(res), new GetRestrictedNames(getNamesRequest)];
    }),
    catchError((error: any) => {
      return of(new CreateRestrictedNameFailure(error.message));
    })
  );

  @Effect() editRestrictedName$: Observable<Action> = this.actions$.pipe(
    ofType<EditRestrictedName>(OwnRestrictedListActionTypes.EDIT_RESTRICTED_NAME),
    mergeMap((res: EditRestrictedName) => {
      const editNamesRequest: EditOwnRestrictedNameRequest = <EditOwnRestrictedNameRequest>{
        nameId: res.payload.nameId,
        data: res.payload.data
      };

      return zip(of(res.payload), this.ownRestrictedListService.editRestrictedName(editNamesRequest));
    }),
    switchMap(([payload, res]: [EditOwnRestrictedNamePayload, OwnRestrictedName]) => {
      this.snackBar.success('Name successfully updated');

      const getNamesRequest: GetOwnRestrictedNamesRequest = <GetOwnRestrictedNamesRequest>{
        listId: res.ownSublist
      };

      if (payload.tableSettings) {
        getNamesRequest.data = payload.tableSettings;
      } else {
        getNamesRequest.data = <BaseListReqParams>{
          search: '',
          sort: '-active, -updated_at',
          offset: 0,
          limit: 5,
        };
      }

      return [new EditRestrictedNameSuccess(res), new GetRestrictedNames(getNamesRequest)];
    }),
    catchError((error: any) => {
      return of(new EditRestrictedNameFailure(error.message));
    })
  );
}
